<template>
  <DashboardLayout>
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <Link :href="route('tickets.index')" class="text-sm text-gray-600 hover:text-gray-900 flex items-center mb-4">
          <ArrowLeftIcon class="h-4 w-4 mr-1" />
          Back to Tickets
        </Link>
        <h1 class="text-3xl font-bold text-gray-900">
          {{ isEditMode ? 'Edit Ticket' : 'Create New Ticket' }}
        </h1>
        <p class="text-gray-600 mt-1">
          {{ isEditMode ? 'Update ticket information' : 'Submit a new support ticket' }}
        </p>
      </div>

      <form @submit.prevent="submitForm">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Main Form -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Template Selector (Create Only) -->
            <Card v-if="!isEditMode && templates.length > 0">
              <div class="space-y-3">
                <label class="block text-sm font-medium text-gray-700">
                  Use Template (Optional)
                </label>
                <Select
                  v-model="selectedTemplate"
                  :options="templateOptions"
                  placeholder="Select a template..."
                  @update:modelValue="applyTemplate"
                />
                <p class="text-xs text-gray-500">
                  Templates can help you quickly fill in common ticket types
                </p>
              </div>
            </Card>

            <!-- Basic Information -->
            <Card>
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
              <div class="space-y-4">
                <!-- Subject -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span class="text-red-500">*</span>
                  </label>
                  <Input
                    v-model="form.subject"
                    placeholder="Brief description of the issue"
                    :error="form.errors.subject"
                    maxlength="500"
                  />
                  <div class="flex justify-between mt-1">
                    <p v-if="form.errors.subject" class="text-sm text-red-600">
                      {{ form.errors.subject }}
                    </p>
                    <p class="text-xs text-gray-500 ml-auto">
                      {{ form.subject.length }}/500
                    </p>
                  </div>
                </div>

                <!-- Description -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Description <span class="text-red-500">*</span>
                  </label>
                  <textarea
                    v-model="form.description"
                    rows="8"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                    :class="{ 'border-red-500': form.errors.description }"
                    placeholder="Provide detailed information about the issue..."
                  ></textarea>
                  <div class="flex justify-between mt-1">
                    <p v-if="form.errors.description" class="text-sm text-red-600">
                      {{ form.errors.description }}
                    </p>
                    <p class="text-xs text-gray-500 ml-auto">
                      {{ form.description.length }} characters (min: 10)
                    </p>
                  </div>
                </div>

                <!-- Priority -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Priority <span class="text-red-500">*</span>
                  </label>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <button
                      v-for="priority in priorityOptions"
                      :key="priority.value"
                      type="button"
                      @click="form.priority = priority.value"
                      class="relative p-3 border-2 rounded-lg text-center transition"
                      :class="[
                        form.priority === priority.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      ]"
                    >
                      <component
                        :is="priority.icon"
                        class="h-5 w-5 mx-auto mb-1"
                        :class="getPriorityColor(priority.value)"
                      />
                      <span class="text-sm font-medium">{{ priority.label }}</span>
                      <span
                        v-if="form.priority === priority.value"
                        class="absolute top-2 right-2"
                      >
                        <CheckCircleIcon class="h-5 w-5 text-primary-500" />
                      </span>
                    </button>
                  </div>
                  <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600">
                    {{ form.errors.priority }}
                  </p>
                </div>
              </div>
            </Card>

            <!-- Categorization -->
            <Card>
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Categorization</h2>
              <div class="space-y-4">
                <!-- Category -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Category <span class="text-red-500">*</span>
                  </label>
                  <Select
                    v-model="form.category_id"
                    :options="categoryOptions"
                    placeholder="Select a category"
                    :error="form.errors.category_id"
                    @update:modelValue="onCategoryChange"
                  />
                  <p v-if="form.errors.category_id" class="mt-1 text-sm text-red-600">
                    {{ form.errors.category_id }}
                  </p>
                </div>

                <!-- Subcategory -->
                <div v-if="subcategoryOptions.length > 0">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory
                  </label>
                  <Select
                    v-model="form.subcategory_id"
                    :options="subcategoryOptions"
                    placeholder="Select a subcategory (optional)"
                    :error="form.errors.subcategory_id"
                  />
                  <p v-if="form.errors.subcategory_id" class="mt-1 text-sm text-red-600">
                    {{ form.errors.subcategory_id }}
                  </p>
                </div>

                <!-- Tags -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <div class="space-y-2">
                    <div class="flex space-x-2">
                      <Input
                        v-model="newTag"
                        placeholder="Add a tag..."
                        @keydown.enter.prevent="addTag"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        @click="addTag"
                      >
                        Add
                      </Button>
                    </div>
                    <div v-if="form.tags.length > 0" class="flex flex-wrap gap-2">
                      <Badge
                        v-for="(tag, index) in form.tags"
                        :key="index"
                        variant="neutral"
                        removable
                        @remove="removeTag(index)"
                      >
                        {{ tag }}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <!-- Attachments -->
            <Card>
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Attachments</h2>
              <div class="space-y-4">
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition cursor-pointer"
                  @click="$refs.fileInput.click()"
                  @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="handleFileDrop"
                  :class="{ 'border-primary-400 bg-primary-50': isDragging }"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                    @change="handleFileSelect"
                    class="hidden"
                  />
                  <CloudArrowUpIcon class="mx-auto h-12 w-12 text-gray-400" />
                  <p class="mt-2 text-sm text-gray-600">
                    <span class="font-medium text-primary-600">Click to upload</span>
                    or drag and drop
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    JPG, PNG, PDF, DOC, XLS, TXT, CSV, ZIP up to 10MB each
                  </p>
                </div>

                <!-- File List -->
                <div v-if="form.attachments.length > 0" class="space-y-2">
                  <div
                    v-for="(file, index) in form.attachments"
                    :key="index"
                    class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div class="flex items-center space-x-3 flex-1 min-w-0">
                      <DocumentIcon class="h-8 w-8 text-gray-400 flex-shrink-0" />
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 truncate">
                          {{ file.name }}
                        </p>
                        <div class="flex items-center space-x-2">
                          <p class="text-xs text-gray-500">
                            {{ formatFileSize(file.size) }}
                          </p>
                          <span
                            v-if="uploadProgress[index] !== undefined"
                            class="text-xs text-gray-500"
                          >
                            • {{ uploadProgress[index] }}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Progress Bar -->
                    <div
                      v-if="uploadProgress[index] !== undefined && uploadProgress[index] < 100"
                      class="flex-1 mx-4"
                    >
                      <div class="w-full bg-gray-200 rounded-full h-2">
                        <div
                          class="bg-primary-600 h-2 rounded-full transition-all"
                          :style="{ width: `${uploadProgress[index]}%` }"
                        ></div>
                      </div>
                    </div>

                    <!-- Remove Button -->
                    <button
                      type="button"
                      @click="removeFile(index)"
                      class="text-red-600 hover:text-red-700 p-1"
                    >
                      <XMarkIcon class="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <p v-if="form.errors.attachments" class="text-sm text-red-600">
                  {{ form.errors.attachments }}
                </p>
              </div>
            </Card>
          </div>

          <!-- Sidebar -->
          <div class="space-y-6">
            <!-- Assignment & Scheduling -->
            <Card>
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Assignment</h2>
              <div class="space-y-4">
                <!-- Assigned To -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To
                  </label>
                  <Select
                    v-model="form.assigned_to"
                    :options="userOptions"
                    placeholder="Auto-assign or select user"
                  >
                    <template #selected v-if="form.assigned_to && autoAssignSuggestion">
                      <div class="flex items-center space-x-2">
                        <Avatar
                          :src="getSelectedUser(form.assigned_to)?.profile_photo"
                          :name="getSelectedUser(form.assigned_to)?.full_name"
                          size="xs"
                        />
                        <span>{{ getSelectedUser(form.assigned_to)?.full_name }}</span>
                        <Badge v-if="autoAssignSuggestion === form.assigned_to" variant="info" size="xs">
                          Auto
                        </Badge>
                      </div>
                    </template>
                  </Select>
                  <p v-if="autoAssignSuggestion" class="mt-1 text-xs text-blue-600">
                    <InformationCircleIcon class="h-3 w-3 inline mr-1" />
                    Auto-assigned based on category
                  </p>
                </div>

                <!-- Due Date -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Due Date
                  </label>
                  <Input
                    v-model="form.due_date"
                    type="date"
                    :min="minDate"
                    :error="form.errors.due_date"
                  />
                  <p v-if="form.errors.due_date" class="mt-1 text-sm text-red-600">
                    {{ form.errors.due_date }}
                  </p>
                </div>

                <!-- Internal Toggle -->
                <div v-if="canCreateInternal">
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      v-model="form.is_internal"
                      type="checkbox"
                      class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span class="text-sm font-medium text-gray-700">Internal Ticket</span>
                  </label>
                  <p class="text-xs text-gray-500 mt-1 ml-6">
                    Internal tickets are only visible to staff members
                  </p>
                </div>
              </div>
            </Card>

            <!-- SLA Preview -->
            <Card v-if="slaPreview">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">SLA Preview</h2>
              <div class="space-y-3">
                <div class="flex items-start space-x-2">
                  <ClockIcon class="h-5 w-5 text-gray-400 mt-0.5" />
                  <div class="flex-1">
                    <p class="text-sm font-medium text-gray-700">Response Time</p>
                    <p class="text-sm text-gray-600">
                      {{ formatSlaTime(slaPreview.response_time_minutes) }}
                    </p>
                  </div>
                </div>
                <div class="flex items-start space-x-2">
                  <CheckCircleIcon class="h-5 w-5 text-gray-400 mt-0.5" />
                  <div class="flex-1">
                    <p class="text-sm font-medium text-gray-700">Resolution Time</p>
                    <p class="text-sm text-gray-600">
                      {{ formatSlaTime(slaPreview.resolution_time_minutes) }}
                    </p>
                  </div>
                </div>
                <div v-if="slaPreview.business_hours_only" class="flex items-start space-x-2">
                  <InformationCircleIcon class="h-5 w-5 text-blue-500 mt-0.5" />
                  <div class="flex-1">
                    <p class="text-xs text-gray-600">
                      Calculated during business hours only
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <!-- Draft Status -->
            <Card v-if="!isEditMode && hasDraft">
              <div class="flex items-start space-x-2">
                <DocumentDuplicateIcon class="h-5 w-5 text-blue-500 mt-0.5" />
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-900">Draft Saved</p>
                  <p class="text-xs text-gray-600 mt-1">
                    Last saved {{ formatRelativeTime(draftTimestamp) }}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="mt-2"
                    @click="clearDraft"
                  >
                    Clear Draft
                  </Button>
                </div>
              </div>
            </Card>

            <!-- Actions -->
            <Card>
              <div class="space-y-3">
                <Button
                  type="submit"
                  class="w-full"
                  :loading="form.processing"
                  :disabled="!isFormValid"
                >
                  <PaperAirplaneIcon class="h-5 w-5 mr-2" />
                  {{ isEditMode ? 'Update Ticket' : 'Submit Ticket' }}
                </Button>

                <Button
                  v-if="!isEditMode"
                  type="button"
                  variant="outline"
                  class="w-full"
                  @click="saveDraft"
                  :disabled="form.processing"
                >
                  <DocumentIcon class="h-5 w-5 mr-2" />
                  Save Draft
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  class="w-full"
                  @click="handleCancel"
                >
                  Cancel
                </Button>
              </div>
            </Card>

            <!-- Help Text -->
            <Card>
              <div class="space-y-2">
                <h3 class="text-sm font-semibold text-gray-900">Need Help?</h3>
                <ul class="text-xs text-gray-600 space-y-1">
                  <li>• Be as specific as possible in your description</li>
                  <li>• Include error messages if applicable</li>
                  <li>• Attach screenshots or relevant files</li>
                  <li>• Select the appropriate priority level</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>

    <!-- Unsaved Changes Modal -->
    <Modal :show="showUnsavedModal" @close="showUnsavedModal = false" max-width="md">
      <template #title>Unsaved Changes</template>
      <template #content>
        <p class="text-sm text-gray-600">
          You have unsaved changes. Do you want to save them as a draft before leaving?
        </p>
      </template>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <Button variant="outline" @click="discardChanges">
            Discard
          </Button>
          <Button @click="saveDraftAndLeave">
            Save Draft
          </Button>
        </div>
      </template>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { router, Link, useForm } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Input from '@/Components/Common/Input.vue';
import Select from '@/Components/Common/Select.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ClockIcon,
  InformationCircleIcon,
  DocumentDuplicateIcon,
  FlagIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
} from '@heroicons/vue/24/outline';
import { formatFileSize, formatRelativeTime } from '@/Utils/helpers';
import { usePermissions } from '@/Composables/usePermissions';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  ticket: {
    type: Object,
    default: null,
  },
  categories: {
    type: Array,
    default: () => [],
  },
  users: {
    type: Array,
    default: () => [],
  },
  templates: {
    type: Array,
    default: () => [],
  },
  slaPolicy: {
    type: Object,
    default: null,
  },
});

const { can } = usePermissions();
const { showToast } = useToast();

// State
const fileInput = ref(null);
const isDragging = ref(false);
const newTag = ref('');
const selectedTemplate = ref(null);
const autoAssignSuggestion = ref(null);
const slaPreview = ref(props.slaPolicy);
const uploadProgress = ref({});
const showUnsavedModal = ref(false);
const hasDraft = ref(false);
const draftTimestamp = ref(null);

// Computed
const isEditMode = computed(() => !!props.ticket);
const canCreateInternal = computed(() => can('it_helpdesk.tickets.create_internal'));

const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

// Form
const form = useForm({
  subject: props.ticket?.subject || '',
  description: props.ticket?.description || '',
  priority: props.ticket?.priority || 'normal',
  category_id: props.ticket?.category_id || null,
  subcategory_id: props.ticket?.subcategory_id || null,
  assigned_to: props.ticket?.assigned_to?.id || null,
  due_date: props.ticket?.due_date || '',
  attachments: [],
  is_internal: props.ticket?.is_internal || false,
  tags: props.ticket?.tags || [],
});

// Options
const priorityOptions = [
  {
    value: 'low',
    label: 'Low',
    icon: MinusCircleIcon,
    description: 'Can wait for normal queue processing',
  },
  {
    value: 'normal',
    label: 'Normal',
    icon: ExclamationCircleIcon,
    description: 'Standard priority ticket',
  },
  {
    value: 'high',
    label: 'High',
    icon: ExclamationTriangleIcon,
    description: 'Important but not urgent',
  },
  {
    value: 'urgent',
    label: 'Urgent',
    icon: FlagIcon,
    description: 'Requires immediate attention',
  },
];

const categoryOptions = computed(() => {
  const parentCategories = props.categories.filter(cat => !cat.parent_id);
  return [
    { value: null, label: 'Select a category' },
    ...parentCategories.map(cat => ({
      value: cat.id,
      label: cat.name,
    })),
  ];
});

const subcategoryOptions = computed(() => {
  if (!form.category_id) return [];

  const subcategories = props.categories.filter(
    cat => cat.parent_id === form.category_id
  );

  return [
    { value: null, label: 'None' },
    ...subcategories.map(cat => ({
      value: cat.id,
      label: cat.name,
    })),
  ];
});

const userOptions = computed(() => [
  { value: null, label: 'Unassigned' },
  ...props.users.map(user => ({
    value: user.id,
    label: user.full_name,
  })),
]);

const templateOptions = computed(() => [
  { value: null, label: 'No template' },
  ...props.templates.map(template => ({
    value: template.id,
    label: template.name,
    data: template,
  })),
]);

const isFormValid = computed(() => {
  return (
    form.subject.trim().length > 0 &&
    form.description.trim().length >= 10 &&
    form.priority &&
    form.category_id
  );
});

// Methods
const getPriorityColor = (priority) => {
  const colors = {
    low: 'text-gray-500',
    normal: 'text-blue-500',
    high: 'text-orange-500',
    urgent: 'text-red-500',
  };
  return colors[priority] || 'text-gray-500';
};

const getSelectedUser = (userId) => {
  return props.users.find(u => u.id === userId);
};

const onCategoryChange = (categoryId) => {
  // Reset subcategory when category changes
  form.subcategory_id = null;

  // Check for auto-assignment
  const category = props.categories.find(cat => cat.id === categoryId);
  if (category?.default_assignee_id) {
    autoAssignSuggestion.value = category.default_assignee_id;
    form.assigned_to = category.default_assignee_id;
  } else {
    autoAssignSuggestion.value = null;
  }

  // Update SLA preview
  updateSlaPreview(categoryId);
};

const updateSlaPreview = (categoryId) => {
  // Find category
  const category = props.categories.find(cat => cat.id === categoryId);

  if (category?.sla_policy) {
    slaPreview.value = category.sla_policy;
  } else {
    // Use priority-based SLA
    // This would normally come from the server, for now use default
    slaPreview.value = props.slaPolicy;
  }
};

const formatSlaTime = (minutes) => {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours < 24) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours} hours`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return remainingHours > 0
    ? `${days}d ${remainingHours}h`
    : `${days} days`;
};

const applyTemplate = (templateId) => {
  if (!templateId) return;

  const template = props.templates.find(t => t.id === templateId);
  if (!template) return;

  // Apply template values
  if (template.subject_template) {
    form.subject = replaceTemplateVariables(template.subject_template);
  }
  if (template.description_template) {
    form.description = replaceTemplateVariables(template.description_template);
  }
  if (template.priority) {
    form.priority = template.priority;
  }
  if (template.category_id) {
    form.category_id = template.category_id;
    onCategoryChange(template.category_id);
  }

  showToast({
    type: 'success',
    message: 'Template applied successfully.',
  });

  // Reset selection
  setTimeout(() => {
    selectedTemplate.value = null;
  }, 100);
};

const replaceTemplateVariables = (text) => {
  // Replace common template variables
  const variables = {
    '{{date}}': new Date().toLocaleDateString(),
    '{{time}}': new Date().toLocaleTimeString(),
    '{{user_name}}': 'User', // Would come from auth
    '{{company}}': 'Company', // Would come from company context
  };

  let result = text;
  Object.entries(variables).forEach(([key, value]) => {
    result = result.replace(new RegExp(key, 'g'), value);
  });

  return result;
};

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files);
  addFiles(files);
};

const handleFileDrop = (event) => {
  isDragging.value = false;
  const files = Array.from(event.dataTransfer.files);
  addFiles(files);
};

const addFiles = (files) => {
  // Validate file size (10MB max)
  const maxSize = 10 * 1024 * 1024; // 10MB
  const validFiles = files.filter(file => {
    if (file.size > maxSize) {
      showToast({
        type: 'error',
        message: `${file.name} is too large. Maximum file size is 10MB.`,
      });
      return false;
    }
    return true;
  });

  // Add files
  form.attachments.push(...validFiles);

  // Simulate upload progress (in real app, this would be actual upload)
  validFiles.forEach((file, index) => {
    const fileIndex = form.attachments.length - validFiles.length + index;
    simulateUploadProgress(fileIndex);
  });
};

const simulateUploadProgress = (index) => {
  uploadProgress.value[index] = 0;
  const interval = setInterval(() => {
    if (uploadProgress.value[index] >= 100) {
      clearInterval(interval);
    } else {
      uploadProgress.value[index] += 10;
    }
  }, 100);
};

const removeFile = (index) => {
  form.attachments.splice(index, 1);
  delete uploadProgress.value[index];
};

const addTag = () => {
  const tag = newTag.value.trim();
  if (tag && !form.tags.includes(tag)) {
    form.tags.push(tag);
    newTag.value = '';
  }
};

const removeTag = (index) => {
  form.tags.splice(index, 1);
};

const submitForm = () => {
  const formData = new FormData();

  // Add regular fields
  Object.keys(form.data()).forEach(key => {
    if (key === 'attachments') return;
    if (key === 'tags') {
      form.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });
      return;
    }

    const value = form[key];
    if (value !== null && value !== undefined && value !== '') {
      formData.append(key, value);
    }
  });

  // Add attachments
  form.attachments.forEach((file, index) => {
    formData.append(`attachments[${index}]`, file);
  });

  const url = isEditMode.value
    ? route('tickets.update', props.ticket.id)
    : route('tickets.store');

  const method = isEditMode.value ? 'post' : 'post';

  if (isEditMode.value) {
    formData.append('_method', 'PUT');
  }

  form
    .transform(() => formData)
    [method](url, {
      preserveState: true,
      onSuccess: () => {
        clearDraft();
        showToast({
          type: 'success',
          message: isEditMode.value
            ? 'Ticket updated successfully.'
            : 'Ticket created successfully.',
        });
      },
      onError: (errors) => {
        showToast({
          type: 'error',
          message: 'Please fix the errors and try again.',
        });
      },
    });
};

const saveDraft = () => {
  const draft = {
    subject: form.subject,
    description: form.description,
    priority: form.priority,
    category_id: form.category_id,
    subcategory_id: form.subcategory_id,
    assigned_to: form.assigned_to,
    due_date: form.due_date,
    is_internal: form.is_internal,
    tags: form.tags,
    timestamp: Date.now(),
  };

  localStorage.setItem('ticket_draft', JSON.stringify(draft));
  hasDraft.value = true;
  draftTimestamp.value = Date.now();

  showToast({
    type: 'success',
    message: 'Draft saved successfully.',
  });
};

const loadDraft = () => {
  const draftJson = localStorage.getItem('ticket_draft');
  if (!draftJson) return;

  try {
    const draft = JSON.parse(draftJson);

    // Only load draft if not in edit mode and form is empty
    if (!isEditMode.value && !form.subject) {
      form.subject = draft.subject || '';
      form.description = draft.description || '';
      form.priority = draft.priority || 'normal';
      form.category_id = draft.category_id || null;
      form.subcategory_id = draft.subcategory_id || null;
      form.assigned_to = draft.assigned_to || null;
      form.due_date = draft.due_date || '';
      form.is_internal = draft.is_internal || false;
      form.tags = draft.tags || [];

      hasDraft.value = true;
      draftTimestamp.value = draft.timestamp;

      showToast({
        type: 'info',
        message: 'Draft loaded from previous session.',
      });
    }
  } catch (e) {
    console.error('Failed to load draft:', e);
  }
};

const clearDraft = () => {
  localStorage.removeItem('ticket_draft');
  hasDraft.value = false;
  draftTimestamp.value = null;
};

const handleCancel = () => {
  if (hasUnsavedChanges()) {
    showUnsavedModal.value = true;
  } else {
    router.visit(route('tickets.index'));
  }
};

const hasUnsavedChanges = () => {
  if (isEditMode.value) {
    return (
      form.subject !== props.ticket?.subject ||
      form.description !== props.ticket?.description ||
      form.priority !== props.ticket?.priority ||
      form.category_id !== props.ticket?.category_id
    );
  }

  return form.subject || form.description.length > 0;
};

const saveDraftAndLeave = () => {
  saveDraft();
  showUnsavedModal.value = false;
  router.visit(route('tickets.index'));
};

const discardChanges = () => {
  clearDraft();
  showUnsavedModal.value = false;
  router.visit(route('tickets.index'));
};

// Auto-save draft
let autoSaveTimer = null;
watch(
  () => [
    form.subject,
    form.description,
    form.priority,
    form.category_id,
    form.subcategory_id,
    form.assigned_to,
    form.due_date,
    form.is_internal,
    form.tags,
  ],
  () => {
    if (!isEditMode.value && (form.subject || form.description)) {
      clearTimeout(autoSaveTimer);
      autoSaveTimer = setTimeout(() => {
        saveDraft();
      }, 5000); // Auto-save after 5 seconds of inactivity
    }
  },
  { deep: true }
);

// Load draft on mount
onMounted(() => {
  if (!isEditMode.value) {
    loadDraft();
  }
});

// Cleanup
onBeforeUnmount(() => {
  clearTimeout(autoSaveTimer);
});

// Warn before leaving with unsaved changes
onMounted(() => {
  window.addEventListener('beforeunload', (e) => {
    if (hasUnsavedChanges() && !form.processing) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
});
</script>

<style scoped>
/* Custom styles for better UX */
textarea {
  font-family: inherit;
}
</style>
